import {
  useV2CreateAttachmentTypeMutation,
  useV2UpdateAttachmentTypeMutation,
  useV2DeleteAttachmentTypeMutation,
} from "@generated";
import type { V2AttachmentTypeResource } from "@generated";
import type { AttachmentTypeFormValues } from "../types";

// Build the JSON:API `data` body for create/update. The `account` relationship
// required by V2AttachmentTypeResource is inferred from auth server-side, so we
// omit it and cast (same pattern as the Currencies/BusinessUnits MSPAs). The
// enum is written as kebab `document-type` to match the wire format.
export const buildAttachmentTypeData = (
  values: AttachmentTypeFormValues,
  id?: string
): V2AttachmentTypeResource => {
  return {
    type: "attachment-types",
    ...(id ? { id: Number(id) } : {}),
    attributes: {
      name: values.name,
      "document-type": values.documentType,
    },
  } as unknown as V2AttachmentTypeResource;
};

export const useAttachmentTypeMutations = () => {
  const [create, createState] = useV2CreateAttachmentTypeMutation();
  const [update, updateState] = useV2UpdateAttachmentTypeMutation();
  const [destroy, destroyState] = useV2DeleteAttachmentTypeMutation();

  return {
    create: (values: AttachmentTypeFormValues) =>
      create({ body: { data: buildAttachmentTypeData(values) } }).unwrap(),

    update: (id: string, values: AttachmentTypeFormValues) =>
      update({
        id: Number(id),
        body: { data: buildAttachmentTypeData(values, id) },
      }).unwrap(),

    // Archive (soft-delete: the API stamps deleted_at; restore re-activates).
    archive: (id: string) => destroy({ id: Number(id) }).unwrap(),

    // `active` is not in the generated V2AttachmentTypeResource schema; the cast
    // lets restore re-activate a soft-deleted record (same pattern as Currencies).
    restore: (id: string, name: string) =>
      update({
        id: Number(id),
        body: {
          data: {
            type: "attachment-types",
            id: Number(id),
            attributes: { name, active: true },
          } as unknown as V2AttachmentTypeResource,
        },
      }).unwrap(),

    states: { createState, updateState, destroyState },
  };
};

export default useAttachmentTypeMutations;
